import React, { useEffect, useState } from "react"
import { Link } from "components/Link"
import { ChevronDown } from "components/SVG"
import Markdown from "markdown-to-jsx"
import { useSegment } from "tracking"
import { color } from "lib/theme/theme.css"
import { readMoreWrapper, readMoreLink, descriptionCopy } from "./ReadMore.css"

type Props = {
  text: string
  location?: string
}

const scrollThreshold = 120

export const ReadMore: React.FC<Props> = ({ text, location = "" }) => {
  const [isCollapsed, setIsCollapsed] = useState(true)
  const [hasOverflow, setHasOverflow] = useState(false)
  const textRef = React.createRef<HTMLDivElement>()
  const { track } = useSegment()

  const toggleReadMore = () => {
    setIsCollapsed(!isCollapsed)
    if (isCollapsed) {
      track({
        event: "Page Element Clicked",
        properties: {
          module_content: `${location} Read More`,
        },
      })
    }
  }

  useEffect(() => {
    if (textRef?.current) {
      const { offsetHeight } = textRef.current

      if (offsetHeight > scrollThreshold) {
        setHasOverflow(true)
      }
    }
  }, [textRef])

  let readMore = (
    <ChevronDown
      stroke={color.contentSecondary}
      strokeWidth="1"
      style={{ rotate: "180deg" }}
    />
  )
  if (isCollapsed && hasOverflow) {
    readMore = <ChevronDown stroke={color.contentSecondary} strokeWidth="1" />
  }

  const styleVariant = isCollapsed && hasOverflow ? "chevron" : "default"

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        ref={textRef}
        className={readMoreWrapper[styleVariant]}
        onClick={() => {
          if (hasOverflow) toggleReadMore()
        }}
        onKeyDown={() => {
          if (hasOverflow) toggleReadMore()
        }}
      >
        <Markdown
          options={{
            overrides: {
              span: {
                component: "span",
                props: {
                  className: descriptionCopy.default,
                },
              },
              p: {
                component: "p",
                props: {
                  className: descriptionCopy.default,
                },
              },
              a: {
                component: Link,
                props: {
                  className: descriptionCopy.link,
                },
              },
            },
          }}
        >
          {text}
        </Markdown>
      </div>
      {hasOverflow && (
        <button
          type="button"
          data-testid="ReadMore"
          className={readMoreLink}
          onClick={() => toggleReadMore()}
          onKeyDown={() => toggleReadMore()}
          title={`${isCollapsed ? "Read more" : "Read less"}`}
        >
          {readMore}
        </button>
      )}
    </>
  )
}
